import { Component, inject, OnInit, OnDestroy, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Subject, takeUntil, filter, distinctUntilChanged, map } from 'rxjs';
import { trigger, transition, style, animate } from '@angular/animations';
import { GuideTourService } from '../../core/services/guide-tour.service';
import { GuideTourAction, GUIDE_TOUR_QUERY_PARAM, GUIDE_TOUR_QUERY_VALUE } from '../../core/models/guide-tour.models';

/**
 * Guide Tour Modal Component
 *
 * A multi-step onboarding modal for new Group Admin users from the self-signup flow.
 * Supports URL-based opening via query parameter and persists state.
 *
 * Features:
 * - 3 steps with illustrations and action buttons
 * - Keyboard navigation (Escape to close, Arrow keys for navigation)
 * - URL-based opening via ?guideTour=open query parameter
 * - Support for actions with query params (e.g., opening CSV dialog)
 * - Responsive design with BEM methodology
 * - Accessibility support with ARIA attributes
 *
 * Usage:
 * 1. Add to app.component.html: <app-guide-tour-modal></app-guide-tour-modal>
 * 2. Open via service: guideTourService.open()
 * 3. Open via URL: /user-management?guideTour=open
 */
@Component({
  selector: 'app-guide-tour-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './guide-tour-modal.component.html',
  styleUrls: ['./guide-tour-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeSlideIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-1rem)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
      transition(':leave', [animate('150ms ease-in', style({ opacity: 0, transform: 'translateY(-0.5rem)' }))]),
    ]),
  ],
})
export class GuideTourModalComponent implements OnInit, OnDestroy {
  /** Injected Guide Tour service */
  public readonly guideTourService = inject(GuideTourService);

  /** Injected ActivatedRoute for URL parameter checking */
  private readonly route = inject(ActivatedRoute);

  /** Subject for managing subscription cleanup */
  private readonly destroy$ = new Subject<void>();

  /** Signal for showing placeholder when image fails to load */
  public showPlaceholder = false;

  /** Computed reference to current step data for template */
  public readonly currentStepData = this.guideTourService.currentStepData;

  /**
   * Flag to prevent reopening modal after user-initiated close.
   * When user clicks an action button that navigates away,
   * we don't want the modal to reopen from query param subscription.
   */
  private hasBeenClosedByAction = false;

  /**
   * Lifecycle hook - initializes component and checks URL params.
   */
  ngOnInit(): void {
    this.checkUrlForGuideTour();
    this.subscribeToRouteChanges();
  }

  /**
   * Lifecycle hook - cleans up subscriptions.
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Keyboard event handler for accessibility.
   * - Escape: Close modal
   * - Left Arrow: Previous step
   * - Right Arrow: Next step
   */
  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.guideTourService.isOpen()) {
      return;
    }

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.onSkip();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.onPrevious();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.onNext();
        break;
    }
  }

  /**
   * Handles click on overlay (outside modal content) to close.
   */
  onOverlayClick(): void {
    this.onSkip();
  }

  /**
   * Prevents click propagation when clicking inside modal content.
   */
  onContentClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  /**
   * Handles skip button click - closes modal and marks as dismissed.
   * User can view the guide later from the UI.
   */
  onSkip(): void {
    this.guideTourService.close(true);
  }

  /**
   * Handles navigation to previous step.
   */
  onPrevious(): void {
    this.showPlaceholder = false;
    this.guideTourService.previousStep();
  }

  /**
   * Handles navigation to next step.
   */
  onNext(): void {
    this.showPlaceholder = false;
    this.guideTourService.nextStep();
  }

  /**
   * Handles action button click.
   * Sets flag to prevent modal reopening from query params,
   * then delegates to service for navigation.
   * @param action - The action configuration
   */
  onActionClick(action: GuideTourAction): void {
    // If this is the last step, mark tour as completed
    if (this.guideTourService.isLastStep()) {
      this.guideTourService.markCompleted();
    }

    // Set flag to prevent reopening from query param subscription
    // This handles the race condition where route subscription might fire
    // before navigation completes
    if (action.closeOnClick) {
      this.hasBeenClosedByAction = true;
    }

    // Use the method that supports query params
    this.guideTourService.handleActionClick(action);
  }

  /**
   * Handles image load error - shows placeholder.
   */
  onImageError(event: Event): void {
    this.showPlaceholder = true;
    const img = event.target as HTMLImageElement;
    if (img) {
      img.style.display = 'none';
    }
  }

  /**
   * Checks URL query parameters for guide tour trigger.
   */
  private checkUrlForGuideTour(): void {
    this.guideTourService.checkUrlAndOpen(this.route);
  }

  /**
   * Subscribes to route query parameter changes.
   * Only opens modal if:
   * - guideTour=open is present
   * - Modal is not already open
   * - Modal wasn't just closed by an action button
   */
  private subscribeToRouteChanges(): void {
    this.route.queryParams
      .pipe(
        takeUntil(this.destroy$),
        // Extract just the guideTour param
        map((params) => params[GUIDE_TOUR_QUERY_PARAM]),
        // Only react to actual changes
        distinctUntilChanged(),
        // Only proceed if param is 'open'
        filter((param) => param === GUIDE_TOUR_QUERY_VALUE),
      )
      .subscribe(() => {
        // Don't reopen if it was just closed by an action
        if (this.hasBeenClosedByAction) {
          this.hasBeenClosedByAction = false;
          return;
        }

        // Only open if not already open
        if (!this.guideTourService.isOpen()) {
          this.guideTourService.open();
        }
      });
  }
}
